// ELPX packaging: an in-memory folder (path -> bytes) <-> .elpx ZIP bytes.
// Pure module (no `vscode` import) so it can be unit tested in Node.
import { unzipSync, zipSync } from 'fflate';

export type Files = Map<string, Uint8Array>;

export const LIMITS = {
    maxEntries: 20_000,
    maxFileBytes: 256 * 1024 * 1024,
    maxTotalBytes: 768 * 1024 * 1024,
};

/** Relative POSIX path that cannot escape the project folder. */
export function isSafePath(path: string): boolean {
    if (!path || path.length > 1024 || /[\0\\:]/.test(path) || path.startsWith('/')) return false;
    return path.split('/').every((segment) => segment !== '' && segment !== '.' && segment !== '..');
}

/**
 * Entries ElpxImporter actually reads: content.xml, screenshot.png, assets
 * (content/resources/, legacy custom/) and the embedded style (theme/).
 * Rendered HTML, libs/ and idevices/ are regenerated by the exporter.
 */
export const IMPORT_FILES = ['content.xml', 'screenshot.png'];
export const IMPORT_DIRS = ['content/resources', 'custom', 'theme'];

export function buildElpx(files: Files): Uint8Array {
    const entries: Record<string, Uint8Array> = {};
    for (const [path, bytes] of files) {
        if (!isSafePath(path)) throw new Error(`Unsafe path: ${path}`);
        entries[path] = bytes;
    }
    // Store only: the archive never leaves memory and most assets are already compressed.
    return zipSync(entries, { level: 0 });
}

/** Unzip untrusted .elpx bytes, rejecting traversal paths and oversized archives. */
export function readElpx(zip: Uint8Array, limits = LIMITS): Files {
    let entries = 0;
    let total = 0;
    const unzipped = unzipSync(zip, {
        filter(file) {
            if (++entries > limits.maxEntries) throw new Error(`Package has more than ${limits.maxEntries} entries`);
            if (file.name.endsWith('/')) return false;
            if (!isSafePath(file.name)) throw new Error(`Unsafe path in package: ${file.name}`);
            total += file.originalSize;
            if (file.originalSize > limits.maxFileBytes || total > limits.maxTotalBytes) {
                throw new Error(`Package is too large (${file.name})`);
            }
            // fflate inflates into a buffer of the declared size, so these checks bound memory.
            return true;
        },
    });
    if (!unzipped['content.xml']) throw new Error('The package has no content.xml');
    return new Map(Object.entries(unzipped));
}

/**
 * eXeLearning (<= 4.0.5 and main as of 2026-10) also writes the project screenshot into
 * content.xml as a base64 `pp_screenshot` property, because the `screenshot` metadata key is
 * missing from its XML exclusion list. screenshot.png already carries it and the importer
 * reads that file, so drop the copy to keep content.xml small and diffable.
 */
export function dropScreenshotProperty(xml: Uint8Array): Uint8Array {
    const text = new TextDecoder('utf-8', { ignoreBOM: true }).decode(xml);
    const cleaned = text.replace(/[ \t]*<odeProperty>\s*<key>pp_screenshot<\/key>\s*<value>[^<]*<\/value>\s*<\/odeProperty>\r?\n?/, '');
    return cleaned === text ? xml : new TextEncoder().encode(cleaned);
}
